import dynamic from "next/dynamic"
import { useRef, useState, useEffect } from "react"
import { useRouter } from "next/router"
import { useForm, FormProvider } from "react-hook-form"
import { searchContent } from "@/lib/api/search"
import { SearchControl } from "@/components/common/form/search-control/search-control"
import { SelectControl } from "@/components/common/form/select-control/select-control"
import { usePathData, useSiteContext } from "@/lib/contexts/appContext"
import Button from "@/components/common/button/button"
import Block from "@/components/wrappers/block"
import Paging from "@/common/paging/paging"
import Loading from "@/components/common/loading/logo-with-text"
import styles from "./archive.module.css"
import Breadcrumbs from "@/components/base/breadcrumbs/breadcrumbs"

const Cards = {
  post: dynamic(() => import("@/common/cards/article/article")),
  event: dynamic(() => import("@/common/cards/event/event")),
  person: dynamic(() => import("@/common/cards/person/person"))
}

const Modals = {
  person: dynamic(() => import("@/common/cards/person/modal"))
}

export default function Archive({ block }) {
  const [firstRequest, setFirstRequest] = useState(true)
  const [hasFiltered, setHasFiltered] = useState(
    block?.attrs?.hasFiltered ?? false
  )
  const [inProgress, setInProgress] = useState(false)
  const [list, setList] = useState(block?.attrs?.list ?? [])
  const [maxPage, setMaxPage] = useState(block?.attrs?.maxPage ?? 1)
  const [page, setPage] = useState(block?.attrs?.page ?? 1)
  const [post, setPost] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [totalCount, setTotalCount] = useState(block?.attrs?.totalCount ?? 0)
  const { heading, description, searchContext, search, resultCopy } =
    block.attrs
  const Modal = Modals[searchContext] ?? NotFound

  const toggleModal = (modalOpen, post = null) => {
    if (modalOpen && post === null) {
      return null
    }

    setModalOpen(modalOpen)
    setPost(post)
  }

  useEffect(() => {
    const handleKeyup = (event) => {
      if (event.keyCode === 27) {
        toggleModal(false)
      }
    }

    window.addEventListener("keyup", handleKeyup)

    return () => {
      window.removeEventListener("keyup", handleKeyup)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Block block={block} width="bleed" gap="none" className={styles.wrapper}>
      <header>
        <Breadcrumbs gap="none" width="full" type="large" />
        <h1>{heading}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: description }}
        />
        <div className="search-wrapper">
          <SearchForm
            data={search}
            {...{
              block,
              searchContext,
              inProgress,
              setInProgress,
              firstRequest,
              page,
              setFirstRequest,
              setList,
              setPage,
              setMaxPage,
              setTotalCount,
              setHasFiltered
            }}
          />
        </div>
      </header>
      <div className="result-wrapper">
        <Heading {...{ inProgress, hasFiltered, resultCopy, totalCount }} />
        <List {...{ list, searchContext, inProgress, toggleModal }} />
        <Modal isOpen={modalOpen} post={post} toggleModal={toggleModal} />
        {!inProgress && list.length > 0 && (
          <div className="paging">
            <Paging currentPage={page} {...{ setPage, maxPage }} />
          </div>
        )}
      </div>
    </Block>
  )
}

const SearchForm = ({
  block,
  searchContext,
  data,
  page,
  inProgress,
  setInProgress,
  firstRequest,
  setFirstRequest,
  setPage,
  setList,
  setMaxPage,
  setTotalCount,
  setHasFiltered
}) => {
  const router = useRouter()
  const pathData = usePathData()
  const siteContext = useSiteContext()
  const methods = useForm({
    defaultValues: {
      search: "",
      category: ""
    },
    mode: "onChange"
  })
  const { control, resetField, watch, getValues, setValue } = methods
  const searchPanel = useRef()

  useEffect(() => {
    if (firstRequest) {
      const query = router.query

      if (query.s) {
        setValue("search", query.s)
      }

      if (query.kategori) {
        setValue("category", query.kategori)
      }

      if (query.page) {
        setPage(parseInt(query.page))
      }

      return
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathData.path])

  useEffect(() => {
    if (firstRequest) {
      setFirstRequest(false)
      return
    }

    doSearch(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const onResetCallback = (name) => {
    resetField(name)
    doSearch(1)
  }

  const doSearch = async (currentPage = 1) => {
    const values = getValues()
    const filters = getFilters(currentPage)

    if (page !== currentPage) {
      setPage(currentPage)
      return
    }

    setFirstRequest(false)
    setInProgress(true)

    // context, string, searchContext, filters
    await searchContent(
      siteContext,
      values?.search,
      searchContext,
      filters
    ).then((response) => {
      setList(response?.list ?? [])
      setTotalCount(response?.totalCount ?? 0)
      setMaxPage(response?.maxPage ?? 0)
      setHasFiltered(false)

      if (values?.search.length > 0 || values?.category.length > 0) {
        setHasFiltered(true)
      }

      pushQuery(currentPage, window.location.pathname)
      setInProgress(false)

      setTimeout(() => {
        searchPanel.current.scrollIntoView({ behavior: "smooth" })
      }, 100)
    })
  }

  const getFilters = (currentPage) => {
    const values = getValues()

    return {
      page: currentPage,
      count: data?.count ?? 16,
      postTypes: searchContext ?? "post",
      organization: block?.attrs?.organization ?? 0,
      taxonomy: data?.taxonomy ?? "",
      term: values?.category ?? ""
    }
  }

  const pushQuery = (currentPage, url, shallow = true) => {
    const query = {}
    const values = getValues()

    if (values.search && values.search.length > 0) {
      query.s = values.search
    }

    if (values.category && values.category.length > 0) {
      query.kategori = values.category
    }

    if (currentPage > 1) {
      query.page = page + ""
    }

    router.push(
      {
        pathname: url,
        query: query
      },
      null,
      { shallow: shallow }
    )
  }

  return (
    <div className="search-panel" ref={searchPanel}>
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            doSearch(1)
          }}>
          <SearchControl
            {...{ control }}
            isLoading={inProgress}
            name="search"
            onReset={onResetCallback}
            placeholder={data.placeholder}
            onSubmit={() => {
              doSearch(1)
            }}
          />
          {data?.type === "select" && (
            <SelectControl
              name={data.taxonomy}
              label={data.label}
              options={data.options}
              onSelect={() => {
                doSearch(1)
              }}
              selected={watch(data.taxonomy)}
            />
          )}
          <Button
            theme="lime"
            type="button"
            loading={false}
            onClick={() => {
              doSearch(1)
            }}
            aria-label="Sök">
            Sök
          </Button>
        </form>
      </FormProvider>
    </div>
  )
}

const List = ({ list, searchContext, inProgress, toggleModal }) => {
  if (inProgress) {
    return (
      <div className="in-progress">
        <Loading />
      </div>
    )
  }

  if (!inProgress && list.length === 0) {
    return (
      <div className="no-result">
        <h2>Hittade ingenting</h2>
        <p>Prova att söka på något annat.</p>
      </div>
    )
  }

  return (
    <ul>
      {list.map((post) => {
        const Card = Cards[searchContext] ?? NotFound

        switch (searchContext) {
          case "person":
            return (
              <Card key={post.id} person={post} toggleModal={toggleModal} />
            )

          default:
            return (
              <li key={post.id}>
                <Card {...{ post }} />
              </li>
            )
        }
      })}
    </ul>
  )
}

const Heading = ({ inProgress, hasFiltered, resultCopy, totalCount }) => {
  if (inProgress || totalCount === 0) {
    return null
  }

  let heading = resultCopy.total + " (" + totalCount + " st.)"

  if (hasFiltered) {
    heading = totalCount === 1 ? resultCopy.singular : resultCopy.plural
    heading = heading.replace("{{count}}", totalCount)
  }

  return <h2 className="result-title">{heading}</h2>
}

const NotFound = () => {
  return null
}
